import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { StaffListItem } from '@/types/settings-api';

/**
 * Active staff list for the onboarding / practice-setup wizard (PROD-1016).
 * GET /api/Staff/StaffSetupList/{organizationId}
 *
 * Flutter parity: `settings_data_source_impl.dart:907-917`
 * (`fetchActiveStaffList`) — path-segment org id, returns a FLAT
 * `List<StaffList>` (Dart `staffListFromJson`). This differs from
 * `useStaffList` (`/api/Staff/StaffList?OrganizationId=&Archive=`), which
 * returns `List<GroupedStaffList>` (`{ roleName, staffList: [...] }` groups)
 * for the role-grouped Settings → Staff tab.
 *
 * The wizard only needs a flat list (for row rendering + the non-empty Next
 * gate), so it consumes this flat endpoint directly — no flattening.
 */
export function useStaffSetupList(
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'staff-setup-list', organizationId],
    queryFn: ({ signal }) => {
      if (!organizationId) {
        throw new Error('useStaffSetupList called without orgId');
      }
      return get<StaffListItem[]>(
        `/api/Staff/StaffSetupList/${organizationId}`,
        { signal },
      );
    },
    enabled: !!organizationId,
  });
}
